import { ProgressBar } from '@arcadiapower/shrike';
import { Button } from '~/components/button';
import { ImageSelector, PAYMENT_IMAGE } from '~/components/image';
import { TwoColumnGrid } from '~/components/layout';
import { Description, Title } from '~/components/typography';
import { GO_SOLAR_FUNNEL_ID } from '~/funnels/go-solar/config';
import { useCopy } from '~/hooks';

export const PaymentHold = ({
  onBack,
  onNext,
  overrideCopy,
  status,
  funnelId,
}: PaymentHoldProps) => {
  const copy = useCopy({}, defaultCopy, overrideCopy);

  function getTimeoutButton() {
    if (funnelId === GO_SOLAR_FUNNEL_ID && status === 'retryOnTimeout') {
      return (
        <Button mode="primary" onClick={onBack} testId="payment-hold-continue-button">
          {copy.retryOnTimeout.buttonText}
        </Button>
      );
    }
    return (
      <Button mode="primary" onClick={onNext} testId="payment-hold-continue-button">
        {copy.timeout.buttonText}
      </Button>
    );
  }

  return (
    <TwoColumnGrid testId="screen-PaymentHold">
      <TwoColumnGrid.Primary>
        <Title>{copy[status].title}</Title>
        <Description>{copy[status].description}</Description>
        {status === 'pending' && <ProgressBar automatic secondsToProgress={15} />}
        {status === 'error' && (
          <Button mode="primary" onClick={onBack} testId="payment-hold-back-button">
            {copy.error.buttonText}
          </Button>
        )}
        {status === 'success' && (
          <Button mode="primary" onClick={onNext} testId="payment-hold-continue-button">
            {copy.success.buttonText}
          </Button>
        )}
        {['timeout', 'retryOnTimeout'].includes(status) && getTimeoutButton()}
      </TwoColumnGrid.Primary>
      <TwoColumnGrid.Secondary>
        <ImageSelector type={copy.image} />
      </TwoColumnGrid.Secondary>
    </TwoColumnGrid>
  );
};

export type PaymentHoldProps = {
  onBack: () => void;
  onNext: () => void;
  funnelId?: string;
  overrideCopy?: (context: any) => Partial<PaymentHoldCopy>;
  status: 'pending' | 'success' | 'error' | 'timeout' | 'retryOnTimeout';
};

export type PaymentHoldCopy = ReturnType<typeof defaultCopy>;

export const defaultCopy = () => ({
  pending: {
    title: 'Verifying your payment method...',
    description: 'Please wait, this could take a few moments.',
  },
  success: {
    title: 'Your payment method was successfully added!',
    description:
      "As a reminder, you may see a a temporary $1 hold on your card. It'll be removed within 24 hours and you won't be charged.",
    buttonText: 'Continue',
  },
  error: {
    title: 'Your payment method is invalid.',
    description: 'Please try again and add another payment method.',
    buttonText: 'Add a new payment method',
  },
  timeout: {
    title: "It's taking a little longer than usual to verify your payment method.",
    description: "That's ok! Click continue to finish signing up.",
    buttonText: 'Continue',
  },
  retryOnTimeout: {
    title: 'Your payment method could not be verified.',
    description: 'Please try again or add another payment method.',
    buttonText: 'Retry',
  },
  image: PAYMENT_IMAGE,
});
